/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { Dropdown } from 'sharedUi/Dropdown';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { agentsService, type AgentListParams } from '../../services/agents.service';
import { projectsService, type AccessibleProject } from '../../services/projects.service';
import { ProjectCell } from '../../components/common/project-cell';
import { useDebounce } from '../../hooks/useDebounce';
import { extractErrorMessage } from '../../utils/error-handler';
import type { Agent } from '../../types';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { useCanCreate, useCanUpdate, useCanDelete, useHasPermission } from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../constants/permissions';
import PlatformSettingsDialog from '../../components/settings/platform-settings-dialog';
import { JOB_NAMES } from '../../constants/jobNames';
import PageHeader from '../../components/common/page-header';
import SaasDataTable, { type SaasColumn, buildDefaultFilters, buildColumnFiltersParam } from '../../components/common/saas-data-table';
import ShowCoreToggle from '../../components/common/show-core-toggle';
import { formatDate } from 'sharedUi/dateUtils';
import { StatusChip } from '../../components/common/panel';

type AgentRow = Agent & { id: string };

/** 40×40 keeps every row action above the desktop hit-area floor. */
const rowAction = 'ui-press flex h-10 w-10 items-center justify-center rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-700';

const FILTER_DEFS: SaasColumn[] = [
  { field: 'agentCode', header: 'Agent Key', filter: true },
  { field: 'name', header: 'Name', filter: true },
  { field: 'status', header: 'Status', filter: true },
];

const AgentList = ({ embedded = false, appId }: { embedded?: boolean; appId?: string }) => {
  const { navigateToView, navigateToEdit, navigateToCreate } = useSecureNavigation({ entity: 'agent', basePath: '/agents' });
  const toast = useRef<Toast>(null);
  const canCreate = useCanCreate(JOB_NAMES.AGENT) && !appId;
  const canEditSettings = useHasPermission(PERMISSIONS.APP_SETTING_UPDATE);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const canUpdate = useCanUpdate(JOB_NAMES.AGENT);
  const canDelete = useCanDelete(JOB_NAMES.AGENT);

  const [rows, setRows] = useState<AgentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [accessibleProjects, setAccessibleProjects] = useState<AccessibleProject[]>([]);
  // Shown for ONE project (the Data Explorer): the screen opens already
  // narrowed to it, instead of to the session's project.
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(appId ?? null);
  const [showCore, setShowCore] = useState(false);
  const canManageProjects = !appId && (accessibleProjects.length > 1);
  useEffect(() => { projectsService.getAccessible().then(setAccessibleProjects).catch(() => setAccessibleProjects([])); }, []);

  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>(() => buildDefaultFilters(FILTER_DEFS));
  const debouncedSearchText = useDebounce(searchText, 500);
  const showError = useCallback((m: string) => toast.current?.show({ severity: 'error', summary: 'Error', detail: m, life: 10000 }), []);

  const fetchRows = useCallback(async (page = 1, size = pageSize, sortBy = sortField, order = sortOrder, activeFilters = filters) => {
    try {
      setLoading(true);
      const params: AgentListParams = { page, page_size: size, include_deleted: false };
      if (debouncedSearchText?.trim()) params.search = debouncedSearchText.trim();
      if (sortBy) { params.sort_by = sortBy; params.sort_order = order === 1 ? 'asc' : 'desc'; }
      const fp = buildColumnFiltersParam(activeFilters); if (fp) params.filters = fp;
      if (selectedProjectId) params.app_id = selectedProjectId;
      params.include_global = showCore;
      const response = await agentsService.list(params);
      setRows((response.data || []).map((a) => ({ ...a, id: a._id })));
      setTotalRecords((response as any).pagination?.totalItems ?? (response as any).pagination?.total ?? response.total ?? 0);
    } catch (e) { showError(extractErrorMessage(e, 'Failed to load agents')); } finally { setLoading(false); }
  }, [debouncedSearchText, selectedProjectId, showCore, pageSize, sortField, sortOrder, filters, showError]);

  const handlePageChange = (e: any) => { setFirst(e.first); setPageSize(e.rows); fetchRows(Math.floor(e.first / e.rows) + 1, e.rows, sortField, sortOrder); };
  const handleSort = (e: any) => { setSortField(e.sortField); setSortOrder(e.sortOrder); setFirst(0); fetchRows(1, pageSize, e.sortField, e.sortOrder); };
  const handleFilter = (e: any) => { const next = e.filters || {}; setFilters(next); setFirst(0); fetchRows(1, pageSize, sortField, sortOrder, next); };
  useEffect(() => { setFirst(0); fetchRows(1, pageSize); }, [debouncedSearchText, selectedProjectId, showCore]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeleteConfirm = async () => {
    if (!selectedId) return;
    try { setDeleteLoading(true); await agentsService.softDelete(selectedId); setDeleteDialogOpen(false); setSelectedId(null); fetchRows(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder); }
    catch (e: any) { setDeleteDialogOpen(false); setSelectedId(null); showError(extractErrorMessage(e, 'Failed to delete the agent')); }
    finally { setDeleteLoading(false); }
  };

  const columns: SaasColumn<AgentRow>[] = useMemo(() => [
    { field: 'agentCode', header: 'Agent Key', sortable: true, filter: true, hideable: false, body: (a) => <span className="font-mono text-xs">{a.agentCode}</span> },
    { field: 'name', header: 'Name', sortable: true, filter: true },
    { field: 'status', header: 'Status', sortable: true, filter: true, body: (a) => <StatusChip tone={a.status === 'ACTIVE' ? 'success' : 'neutral'}>{a.status || 'ACTIVE'}</StatusChip> },
    // The catalogue size is the one number that says whether an agent is worth
    // targeting: an agent eval grades which of these the model reached for.
    { field: 'tools', header: 'Tools', align: 'right', body: (a) => <span className="tabular-nums">{(a.tools || []).length}</span> },
    { field: 'modelRef', header: 'Model', body: (a) => (a.modelRef
        ? <span className="font-mono text-xs text-gray-600 dark:text-gray-300">pinned</span>
        : <span className="text-ink-muted">run&rsquo;s model</span>) },
    // AUTO is stored as absent, so anything here is a deliberate override worth seeing.
    { field: 'toolChoice', header: 'Selection', body: (a) => (a.toolChoice?.mode && a.toolChoice.mode !== 'AUTO'
        ? <StatusChip tone="info" dot={false}>{a.toolChoice.mode}</StatusChip>
        : <span className="text-ink-muted">auto</span>) },
    { field: 'version', header: 'Version', sortable: true, align: 'right',
      body: (a) => <span className="tabular-nums">v{a.version ?? 1}</span> },
    { field: 'updatedAt', header: 'Updated At', sortable: true, body: (a) => <span className="tabular-nums">{formatDate(a.updatedAt || a.createdAt)}</span> },
    { field: 'appId', header: 'Project', body: (a: any) => <ProjectCell appId={a.appId} projects={accessibleProjects} /> },
    { field: 'actions', header: 'Actions', align: 'center', hideable: false, body: (a) => (
      <div className="flex justify-center gap-0.5">
        <button type="button" className={rowAction} onClick={() => navigateToView(a._id)} data-pr-tooltip="View" aria-label="View agent"><i className="pi pi-eye text-emerald-600 dark:text-emerald-400 text-[1.05rem]" aria-hidden /></button>
        {canUpdate && <button type="button" className={rowAction} onClick={() => navigateToEdit(a._id)} data-pr-tooltip="Edit" aria-label="Edit agent"><i className="pi pi-pencil ui-act--edit text-[1.05rem]" aria-hidden /></button>}
        {canDelete && <button type="button" className={rowAction} onClick={() => { setSelectedId(a._id); setDeleteDialogOpen(true); }} data-pr-tooltip="Delete" aria-label="Delete agent"><i className="pi pi-trash ui-act--delete text-[1.05rem]" aria-hidden /></button>}
      </div>
    ) },
  ], [canUpdate, canDelete, accessibleProjects, navigateToView, navigateToEdit]);

  return (
    <PermissionGuard jobName={JOB_NAMES.AGENT}>
      <Toast ref={toast} position="top-right" />
      <div className={embedded ? '' : 'p-6'} data-testid="SCR-Agent-List">
        <PageHeader embedded={embedded} icon="pi pi-android" title="Agents"
          subtitle="Tool catalogues an eval suite can target" testId="Agent.Header" />
        <div className={embedded ? '' : 'ui-panel overflow-hidden'}>
          <SaasDataTable tableId="eval-agent" columns={appId ? columns.filter((c) => c.field !== 'appId') : columns} data={rows} loading={loading} dataKey="_id" emptyMessage="No agents yet — create one to target it from an agent suite"
            first={first} rows={pageSize} totalRecords={totalRecords} onPage={handlePageChange} rowsPerPageOptions={[5, 10, 25, 50]}
            sortField={sortField || undefined} sortOrder={sortOrder || undefined} onSort={handleSort} filters={filters} onFilter={handleFilter}
            globalSearch={searchText} onGlobalSearchChange={setSearchText} globalSearchPlaceholder="Search agents..." testId="AgentList"
            toolbarStart={<div className="flex items-center gap-3 flex-wrap">
              {canManageProjects && <Dropdown value={selectedProjectId} options={accessibleProjects.map((p) => ({ label: p.appCode ? `${p.name} (${p.appCode})` : p.name, value: p._id }))} onChange={(e: any) => setSelectedProjectId(e.value || null)} placeholder="Project" showClear style={{ minWidth: '160px' }} />}
              <ShowCoreToggle checked={showCore} onChange={setShowCore} testId="AgentList.Toggle.ShowCore" />
            </div>}
            toolbarEnd={<><span className="whitespace-nowrap text-sm tabular-nums text-ink-muted">{loading && !rows.length ? 'loading…' : `${totalRecords} ${totalRecords === 1 ? 'record' : 'records'}`}</span>
              <RefreshButton onClick={() => fetchRows(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder)} loading={loading} />
              {canEditSettings && <Button label="Conversation settings" icon="pi pi-sliders-h" size="small" severity="secondary" outlined onClick={() => setSettingsOpen(true)} data-testid="AgentList.Button.Settings" />}
              {canCreate && <span className="ui-press inline-flex"><Button label="Create Agent" icon="pi pi-plus" onClick={() => navigateToCreate()} size="small" /></span>}</>} />
        </div>
        <PlatformSettingsDialog title="Conversation settings" visible={settingsOpen} onHide={() => setSettingsOpen(false)}
          testId="AgentList.Dialog.Settings"
          keys={[{ key: 'Conversation_Turn_Lock_Seconds', label: 'Reply lock timeout', unit: 'seconds' }]} />
        <DeleteConfirmDialog visible={deleteDialogOpen} onCancel={() => { setDeleteDialogOpen(false); setSelectedId(null); }} onDelete={handleDeleteConfirm}
          message="Delete this agent? A suite that targets it must be repointed first." testId="AgentList.Dialog.Delete" loading={deleteLoading} />
      </div>
    </PermissionGuard>
  );
};

export default AgentList;
